import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin } from 'rxjs';

@Component({
  selector: 'app-translation',
  templateUrl: './translation.component.html',
  styleUrls: ['./translation.component.scss']
})
export class TranslationComponent implements OnInit {
  langs: string[] = [];
  selectedLangs: Set<string> = new Set();
  searchText = '';
  allKeys: string[] = [];
  translations: Record<string, Record<string, string>> = {};

  constructor(
    private http: HttpClient,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.langs = this.translate.getLangs();
    if (!this.langs.length) {
      this.langs = ['fr', 'en'];
    }
    this.langs.forEach((l) => this.selectedLangs.add(l));

    const requests: Record<string, any> = {};
    this.langs.forEach((lang) => {
      requests[lang] = this.http.get(`./assets/i18n/${lang}.json`);
    });

    forkJoin(requests).subscribe((results: Record<string, any>) => {
      const keySet = new Set<string>();
      this.langs.forEach((lang) => {
        const flat = this.flattenObject(results[lang]);
        this.translations[lang] = flat;
        Object.keys(flat).forEach((k) => keySet.add(k));
      });
      this.allKeys = Array.from(keySet).sort();
    });
  }

  get filteredKeys(): string[] {
    if (!this.searchText) return this.allKeys;
    const search = this.searchText.toLowerCase();
    return this.allKeys.filter((key) => {
      if (key.toLowerCase().includes(search)) return true;
      for (const lang of Array.from(this.selectedLangs)) {
        const val = this.translations[lang]?.[key];
        if (val && val.toLowerCase().includes(search)) return true;
      }
      return false;
    });
  }

  get selectedLangsArray(): string[] {
    return this.langs.filter((l) => this.selectedLangs.has(l));
  }

  isLangSelected(lang: string): boolean {
    return this.selectedLangs.has(lang);
  }

  toggleLang(lang: string): void {
    if (this.selectedLangs.has(lang)) {
      this.selectedLangs.delete(lang);
    } else {
      this.selectedLangs.add(lang);
    }
  }

  getValue(key: string, lang: string): string {
    return this.translations[lang]?.[key] ?? '';
  }

  downloadCsv(): void {
    const langs = this.selectedLangsArray;
    const headers = ['key', ...langs].join(';');
    const rows = this.filteredKeys.map((key) => {
      const values = langs.map((lang) => {
        const val = this.getValue(key, lang);
        return '"' + val.replace(/"/g, '""') + '"';
      });
      return ['"' + key.replace(/"/g, '""') + '"', ...values].join(';');
    });
    const csv = [headers, ...rows].join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    this.downloadBlob(blob, 'translations.csv');
  }

  downloadJson(lang: string): void {
    this.http.get(`./assets/i18n/${lang}.json`).subscribe((data) => {
      const json = JSON.stringify(data, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      this.downloadBlob(blob, `${lang}.json`);
    });
  }

  private downloadBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  private flattenObject(obj: any, prefix = ''): Record<string, string> {
    const result: Record<string, string> = {};
    for (const key of Object.keys(obj)) {
      const fullKey = prefix ? `${prefix}.${key}` : key;
      if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
        Object.assign(result, this.flattenObject(obj[key], fullKey));
      } else {
        result[fullKey] = String(obj[key]);
      }
    }
    return result;
  }
}
